import { useMemo } from "react";

import { useAlertContext, useFormikWithDirtyForm } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import { SystemDropdownCategoryResponse, SystemDropdownOptionResponse, systemDropdownsApi } from "@/api";
import { nameof } from "@/common/helper/commonHelper";
import {
  SystemDropdownRowValues,
  SystemDropdownsFormValues,
} from "@/pages/system-settings/tabs/system-dropdowns/SystemDropdownsSettings.types";
import { useSortedSystemDropdownCategories } from "@/pages/system-settings/tabs/system-dropdowns/hooks/useSortedSystemDropdownCategories";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useSystemDropdownsSettings() {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();

  const { selectedCategory } = useSortedSystemDropdownCategories();

  const { data: categories = [] } = useQuery<SystemDropdownCategoryResponse[]>({
    queryKey: [QueryKey.SystemDropdownCategories],
    queryFn: () => systemDropdownsApi.getSystemDropdownCategories(),
  });

  // The administration screen is the one place hidden options must stay visible, so they can be
  // reactivated.
  const { data: options = [] } = useQuery<SystemDropdownOptionResponse[]>({
    queryKey: [QueryKey.SystemDropdownOptions, selectedCategory, "admin"],
    queryFn: () => systemDropdownsApi.getSystemDropdownOptions({ category: selectedCategory, includeHidden: true }),
    staleTime: 0,
    refetchOnMount: "always",
  });

  const categoryDefinition = useMemo(
    () => categories.find((category) => category.category === selectedCategory),
    [categories, selectedCategory]
  );

  const initialValues = useMemo<SystemDropdownsFormValues>(
    () => ({
      category: selectedCategory,
      rows: options.map((option) => ({
        id: option.id,
        value: option.value,
        isHidden: option.isHidden,
        parameters: option.parameters.map((p) => ({ parameter: p.parameter, value: p.value })),
      })),
    }),
    [selectedCategory, options]
  );

  const maxValueLength = categoryDefinition?.maxValueLength;

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        rows: Yup.array().of(
          Yup.object().shape({
            // An option can never be deleted, so a blank one has to be rejected rather than dropped:
            // silently discarding it would look like a failed save.
            value: Yup.string()
              .trim()
              .required(t("settings.systemDropdowns.valueRequired"))
              .max(maxValueLength ?? Number.MAX_SAFE_INTEGER, ({ max }) => t("common.validation.maxLength", { max })),
          })
        ),
      }),
    [maxValueLength, t]
  );

  const saveMutation = useMutation({
    mutationFn: (values: SystemDropdownsFormValues) =>
      systemDropdownsApi.upsertSystemDropdownOptions({
        category: values.category,
        upsertSystemDropdownOptionsRequest: {
          options: values.rows.map((row) => ({
            id: row.id,
            value: row.value.trim(),
            isHidden: row.isHidden,
            parameters: row.parameters,
          })),
        },
      }),
    onSuccess: async () => {
      // Every selection list in the app reads these options, so the shared cache must drop too.
      await queryClient.invalidateQueries({ queryKey: [QueryKey.SystemDropdownOptions] });
      showSuccess(t("settings.systemDropdowns.saveSuccess"));
    },
    onError: () => {
      showError(t("settings.systemDropdowns.saveError"));
    },
  });

  const formik = useFormikWithDirtyForm<SystemDropdownsFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: (values) => saveMutation.mutateAsync(values),
  });

  const handleAddRow = () => {
    // A new option inherits the category's parameters at their defaults; the user only sets the values.
    const newRow: SystemDropdownRowValues = {
      id: null,
      value: "",
      isHidden: false,
      parameters: (categoryDefinition?.parameters ?? []).map((definition) => ({
        parameter: definition.parameter,
        value: definition.defaultValue,
      })),
    };

    void formik.setFieldValue(nameof<SystemDropdownsFormValues>("rows"), [...formik.values.rows, newRow]);
  };

  const handleToggleHidden = (index: number) => {
    void formik.setFieldValue(
      nameof<SystemDropdownsFormValues>("rows"),
      formik.values.rows.map((row, i) => (i === index ? { ...row, isHidden: !row.isHidden } : row))
    );
  };

  return {
    formik,
    selectedCategory,
    categoryDefinition,
    maxValueLength,
    isSaving: saveMutation.isPending,
    handleAddRow,
    handleToggleHidden,
  };
}
